import React, {
  useState,
} from 'react';
import {
  selection,
} from 'redux-resource-plugins';
import i18n from 'i18next';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  toast,
} from 'react-toastify';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getUiState,
} from 'web-selectors';

import {
  Modal,
  Button,
  Accordion,
  TreeEntities,
} from 'web-components';

import {
  Flex,
  Text,
  H200,
} from 'web-styled';

import BasicInfo from './BasicInfo';
import * as S from './styled';

const mapState = state => ({
  programs: coreSelectors.getSelectedResourceMappedList(
    'programTemplates',
    'library',
  )(state),
  workouts: coreSelectors.getSelectedResourceMappedList(
    'workoutTemplates',
    'library',
  )(state),
  documents: coreSelectors.getSelectedResourceMappedList(
    'documents',
    'library',
  )(state),
  namespace: getUiState('namespace')(state),
});

interface Product {
  name: string;
  description: string;
  amount: string;
  slug: string;
  type: string;
  period: string;
  trialperiod: string;
}

interface Data {
  name: string;
  description: string;
  amount: string;
  slug: string;
  type: string;
  interval: string;
  trial_period_days: string;
  workout_template_ids: number[],
  program_template_ids: number[],
  document_ids: number[],
}

type Props = {
  isOpen: boolean;
  onRequestCreate: (data: Data) => void;
  onRequestClose: () => void;
};

const initialValues: Product = {
  name: '',
  description: '',
  amount: '',
  slug: '',
  type: 'onetime',
  period: 'monthly',
  trialperiod: '0',
};

const CreateProductModal = ({
  isOpen,
  onRequestCreate,
  onRequestClose,
}: Props) => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<string>('basic');
  const [basicInfo, setBasicInfo] = useState<Product>(initialValues);
  const {
    programs,
    workouts,
    documents,
    namespace,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(
      selection.deselectResources(
        'programTemplates',
        programs.map(p => p.id),
      ),
    );

    dispatch(
      selection.deselectResources(
        'workoutTemplates',
        workouts.map(w => w.id),
      ),
    );

    dispatch(
      selection.deselectResources(
        'documents',
        documents.map(d => d.id),
      ),
    );

    setLoading(false);
    setBasicInfo(initialValues);
    setStep('basic');

    onRequestClose();
  };

  function checkSlug(slug: string, resourceType: string) {
    return new Promise((resolve) => {
      dispatch(
        coreActions.requestProductOrPlanBySlug({
          qs: {
            namespace: namespace.symbol,
          },
          slug,
          resourceType,
          callbacks: {
            onError: () => {
              resolve(false);
            },
            onSuccess: () => {
              resolve(true);
            },
          },
        }),
      );
    });
  }

  const handleCreate = async () => {
    setLoading(true);

    await onRequestCreate({
      name: basicInfo.name,
      description: basicInfo.description,
      amount: basicInfo.amount,
      slug: basicInfo.slug,
      type: basicInfo.type,
      interval: basicInfo.period === 'monthly' ? 'month' : 'year',
      trial_period_days: basicInfo.trialperiod,
      workout_template_ids: workouts.map(w => w.id),
      program_template_ids: programs.map(p => p.id),
      document_ids: documents.map(p => p.id),
    });

    handleClose();
  };

  const onSubmitInfo = async (values, actions) => {
    if (values.slug) {
      const hasProduct = await checkSlug(values.slug, 'products');
      const hasPlan = await checkSlug(values.slug, 'productPlans');
      if (hasProduct || hasPlan) {
        actions.setErrors({ slug: 'URL already exists.' });
        toast.error(
          'URL already exists. Please update the URL field.',
        );
        return;
      }
    }

    setBasicInfo(values);
    actions.setSubmitting(false);
    setStep('content');
  };

  const body: {[k: string]: any} = {
    basic: (
      <BasicInfo
        initialValues={basicInfo}
        onCancel={handleClose}
        onSubmit={onSubmitInfo}
      />
    ),
    content: (
      <Flex
        column
        spacing={30}
        width="100%"
      >
        <H200>Content</H200>
        <Text>
          {i18n.t('product.content.helper')}
        </Text>
        <Accordion
          contents={[
            {
              title: 'Programs',
              subTitle: programs.length ? `(${programs.length} selected)` : null,
              inner: () => (
                <TreeEntities
                  resourceType="programTemplates"
                />
              ),
            },
            {
              title: 'Workouts',
              subTitle: workouts.length ? `(${workouts.length} selected)` : null,
              inner: () => (
                <TreeEntities
                  resourceType="workoutTemplates"
                />
              ),
            },
            {
              title: 'Documents',
              subTitle: documents.length ? `(${documents.length} selected)` : null,
              inner: () => (
                <TreeEntities
                  resourceType="documents"
                />
              ),
            },
          ]}
        />

        <Flex
          width="100%"
          justify="space-between"
          margin="30px 0 0 0"
        >
          <Button
            ghost
            width="140px"
            spacing="large"
            appearance="primary"
            onClick={() => setStep('basic')}
          >
            Previous
          </Button>

          <Button
            width="140px"
            spacing="large"
            appearance="primary"
            onClick={() => setStep('review')}
          >
            Review
          </Button>
        </Flex>
      </Flex>
    ),
    review: (
      <Flex
        column
        spacing={30}
        width="100%"
      >
        <Flex
          column
          spacing={10}
          width="100%"
        >
          <H200>Review</H200>
          <hr />

          <Text
            size="1.28em"
            weight="600"
            whiteSpace="pre-line"
          >
            {basicInfo.name}
          </Text>

          {basicInfo.description && (
            <Text
              whiteSpace="pre-line"
            >
              {basicInfo.description}
            </Text>
          )}

          <Flex align="baseline" spacing={5}>
            <Text weight="600" size="1.28em">
              $
              {basicInfo.amount || 0}
            </Text>

            {basicInfo.type === 'recurring' && (
              <>
                <Text>
                  {basicInfo.period === 'annual' ? 'annually,' : 'per month,'}
                </Text>

                <Text weight="600" size="1.28em">
                  {`${basicInfo.trialperiod || 0} day${basicInfo.trialperiod > 1 ? 's' : ''}`}
                </Text>

                <Text>
                  trial period
                </Text>
              </>
            )}
          </Flex>

          <Text color="link">
            {`${window.location.origin}/${basicInfo.slug}`}
          </Text>
        </Flex>
        <Flex width="100%" column>
          <S.Item>
            <Text size="18px">
              Programs
            </Text>
            <Flex width="100%" column>
              {programs.map(program => (
                <div key={program.id}>
                  {program.name}
                </div>
              ))}
            </Flex>
          </S.Item>
          <S.Item>
            <Text size="18px">
              Workouts
            </Text>
            <Flex width="100%" column>
              {workouts.map(workout => (
                <div key={workout.id}>
                  {workout.name}
                </div>
              ))}
            </Flex>
          </S.Item>
          <S.Item>
            <Text size="18px">
              Documents
            </Text>
            <Flex width="100%" column>
              {documents.map(document => (
                <div key={document.id}>
                  {document.name}
                </div>
              ))}
            </Flex>
          </S.Item>
        </Flex>

        <Flex
          width="100%"
          justify="space-between"
          margin="30px 0 0 0"
        >
          <Button
            ghost
            width="140px"
            spacing="large"
            appearance="primary"
            isDisabled={isLoading}
            onClick={() => setStep('content')}
          >
            Previous
          </Button>

          <Button
            width="140px"
            spacing="large"
            appearance="primary"
            onClick={handleCreate}
            isDisabled={isLoading}
            isLoading={isLoading}
          >
            Finish
          </Button>
        </Flex>
      </Flex>
    ),
  };

  return (
    <Modal
      title="Create Product"
      contentStyle={{
        width: '800px',
      }}
      isOpen={isOpen}
      onRequestClose={handleClose}
    >
      {body[step]}
    </Modal>
  );
};

export default CreateProductModal;
